/**
 * Declarar o NIF e o regime fiscal das duas partes de uma obra.
 *
 * A rota existia há muito e nunca teve ecrã — o comentário dela já dizia que
 * um ecrã de auto-serviço era "o passo seguinte". O resultado prático é que
 * nenhum cliente pessoa singular tinha onde gravar um NIF: as empresas têm o
 * seu no registo, os trabalhadores recebem-no ao inscrever-se, e o cliente
 * não tinha campo nenhum em lado nenhum. Sem NIF do destinatário não se emite
 * factura, e foi aí que a facturação das obras 41 e 45 parou.
 *
 * É a operação que recolhe, e não cada parte que declara, porque é assim que
 * acontece: o NIF aparece quando há uma factura para emitir e é a Dozy quem o
 * pede. Um ecrã de auto-serviço continua a fazer sentido — mas depois deste,
 * não em vez dele.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, ShieldCheck, Search } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';

const TIPOS = [
  { v: 'INDIVIDUAL', r: 'Pessoa singular' },
  { v: 'INDIVIDUAL_BUSINESS', r: 'Empresário em nome individual' },
  { v: 'COMPANY', r: 'Sociedade' },
];

// O Regime de Exclusão (Lei 14/23, art. 60.º) é o que faltava ter nome: o
// prestador está no âmbito do imposto mas dispensado de o liquidar. Tratá-lo
// como "não sujeito" dava o mesmo resultado no cálculo e a razão errada no
// documento.
const REGIMES = [
  { v: 'GENERAL', r: 'Regime geral (liquida IVA)' },
  { v: 'SIMPLIFIED', r: 'Regime simplificado' },
  { v: 'EXCLUSION', r: 'Regime de exclusão (não liquida)' },
  { v: 'NON_SUBJECT', r: 'Não sujeito' },
  { v: 'UNKNOWN', r: 'Por apurar' },
];

const vazio = { nif: '', taxpayer_type: '', vat_regime: 'UNKNOWN', withholding_status: 'UNKNOWN' };

const Parte = ({ titulo, dados, valor, mexer, consultar, aConsultar, agt }) => (
  <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className="text-xs uppercase tracking-wide font-semibold text-slate-500">{titulo}</span>
      <span className="text-sm text-slate-200">{dados.nome}</span>
      {dados.perfil && (
        <span className="text-[11px] text-emerald-500 inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> já declarado
        </span>
      )}
    </div>

    <div className="flex gap-2 flex-wrap">
      <input value={valor.nif} onChange={(e) => mexer({ nif: e.target.value })}
        placeholder="NIF"
        aria-label={`NIF — ${dados.nome}`}
        className="w-40 px-3 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-mono" />
      <select value={valor.taxpayer_type} onChange={(e) => mexer({ taxpayer_type: e.target.value })}
        aria-label={`Tipo fiscal — ${dados.nome}`}
        className="flex-1 min-w-[160px] px-3 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200">
        <option value="">Tipo fiscal…</option>
        {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.r}</option>)}
      </select>
    </div>

    <select value={valor.vat_regime} onChange={(e) => mexer({ vat_regime: e.target.value })}
      aria-label={`Regime de IVA — ${dados.nome}`}
      className="w-full px-3 min-h-[44px] rounded-lg bg-slate-800 border border-slate-700 text-slate-200">
      {REGIMES.map((t) => <option key={t.v} value={t.v}>{t.r}</option>)}
    </select>

    <label className="flex items-start gap-2 text-sm text-slate-300">
      <input type="checkbox" className="mt-1"
        checked={valor.withholding_status === 'SUBJECT'}
        onChange={(e) => mexer({ withholding_status: e.target.checked ? 'SUBJECT' : 'UNKNOWN' })} />
      <span>
        Obrigado a reter na fonte
        <span className="block text-xs text-slate-500">
          Só marcar com certeza: isto decide quem entrega a retenção ao Estado,
          e marcar os dois lados retinha o mesmo imposto duas vezes.
        </span>
      </span>
    </label>

    {dados.nif_conhecido && !valor.nif && (
      <button type="button" onClick={() => mexer({ nif: dados.nif_conhecido })}
        className="text-xs text-blue-400 underline">
        Usar o NIF do registo ({dados.nif_conhecido})
      </button>
    )}

    {/* A AGT responde com o tipo e o regime que estao aqui a ser marcados a
        mao, e ainda diz se o contribuinte esta activo e se esta inadimplente.
        O botao preenche; nao grava. Quem decide continua a ser quem olha. */}
    <div className="pt-2 border-t border-slate-800 space-y-2">
      <button type="button" onClick={consultar}
        disabled={aConsultar || !String(valor.nif || '').trim()}
        className="min-h-[40px] px-3 rounded-lg bg-slate-800 text-slate-200 text-xs
                   inline-flex items-center gap-1.5 disabled:opacity-40">
        {aConsultar
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <Search className="w-3.5 h-3.5" />}
        Consultar na AGT
      </button>

      {agt && !agt.ok && (
        <p className="text-xs text-amber-500">{agt.message}</p>
      )}

      {agt && agt.ok && (
        <div className="text-xs space-y-1">
          <div className="text-slate-300">{agt.nome}</div>
          <div className="text-slate-500">
            {agt.tipo_agt} · {agt.regime_agt || 'regime não indicado'} · {agt.estado}
          </div>
          {(agt.avisos || []).map((a) => (
            <div key={a} className="text-red-400">⚠ {a}</div>
          ))}
          {/* Preenche-se com o que a AGT diz, mas so o que ela disse mesmo:
              um campo vazio no portal nao apaga o que ja la esta. */}
          <button type="button"
            onClick={() => mexer({
              ...(agt.taxpayer_type ? { taxpayer_type: agt.taxpayer_type } : {}),
              ...(agt.vat_regime ? { vat_regime: agt.vat_regime } : {}),
            })}
            className="text-blue-400 underline">
            Preencher com estes dados
          </button>
        </div>
      )}
    </div>
  </div>
);

const PerfilFiscalObra = ({ projectId, aoGravar }) => {
  const { success, error } = useToast();
  const [partes, setPartes] = useState(null);
  const [cliente, setCliente] = useState(vazio);
  const [prestador, setPrestador] = useState(vazio);
  const [busy, setBusy] = useState(false);
  const [agt, setAgt] = useState({});          // resposta da AGT por parte
  const [aConsultar, setAConsultar] = useState(null);

  // A consulta nao grava nada: devolve o que a AGT diz e deixa quem olha
  // decidir. Preencher sozinho um campo fiscal a partir de um portal que pode
  // estar desactualizado era trocar um engano manual por um automatico.
  const consultarAgt = async (qual, nif) => {
    setAConsultar(qual);
    try {
      const { data } = await api.post('/projects/fiscal/consultar-nif', { nif });
      setAgt((a) => ({ ...a, [qual]: data }));
    } catch {
      setAgt((a) => ({ ...a, [qual]: { ok: false, message: 'Nao foi possivel consultar a AGT agora.' } }));
    } finally { setAConsultar(null); }
  };

  const carregar = useCallback(() => {
    api.get(`/projects/${projectId}/fiscal/perfis`)
      .then(({ data }) => {
        setPartes(data);
        const de = (p, fallback) => (p ? {
          nif: p.nif || '', taxpayer_type: p.taxpayer_type || '',
          vat_regime: p.vat_regime || 'UNKNOWN',
          withholding_status: p.withholding_status || 'UNKNOWN',
        } : { ...vazio, taxpayer_type: fallback });
        setCliente(de(data.cliente.perfil, data.cliente.e_empresa ? 'COMPANY' : 'INDIVIDUAL'));
        setPrestador(de(data.prestador.perfil, data.prestador.e_empresa ? 'COMPANY' : 'INDIVIDUAL'));
      })
      .catch(() => setPartes(false));
  }, [projectId]);
  useEffect(() => { carregar(); }, [carregar]);

  const gravar = async () => {
    setBusy(true);
    try {
      // Só se envia o que tem tipo escolhido: o servidor recusa um perfil sem
      // ele, e mandar um lado vazio fazia falhar a gravação do outro.
      const corpo = {};
      if (cliente.taxpayer_type) corpo.cliente = cliente;
      if (prestador.taxpayer_type) corpo.prestador = prestador;
      if (!Object.keys(corpo).length) { error('Escolha pelo menos um tipo fiscal.'); return; }

      await api.post(`/projects/${projectId}/fiscal/perfis`, corpo);
      success('Perfis fiscais declarados. Fica por verificar — quem declara não se aprova.');
      carregar();
      if (aoGravar) aoGravar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível gravar.');
    } finally { setBusy(false); }
  };

  if (partes === null) {
    return <div className="pt-2 border-t border-slate-800 flex justify-center py-4">
      <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
    </div>;
  }
  if (partes === false) return null;   // obra sem contrato: não há partes a declarar

  return (
    <div className="pt-2 border-t border-slate-800 space-y-2">
      <div className="text-slate-300 font-semibold text-sm">Perfil fiscal das partes</div>
      <p className="text-xs text-slate-500">
        É daqui que sai o NIF e o regime de IVA das facturas desta obra. Sem NIF do
        destinatário não se emite factura.
      </p>

      <Parte titulo="Cliente" dados={partes.cliente} valor={cliente}
        mexer={(m) => setCliente((v) => ({ ...v, ...m }))}
        consultar={() => consultarAgt('cliente', cliente.nif)}
        aConsultar={aConsultar === 'cliente'} agt={agt.cliente} />
      <Parte titulo="Executante" dados={partes.prestador} valor={prestador}
        mexer={(m) => setPrestador((v) => ({ ...v, ...m }))}
        consultar={() => consultarAgt('prestador', prestador.nif)}
        aConsultar={aConsultar === 'prestador'} agt={agt.prestador} />

      <button onClick={gravar} disabled={busy}
        className="w-full min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
        <Save className="w-4 h-4" /> Gravar perfis fiscais
      </button>
    </div>
  );
};

export default PerfilFiscalObra;
